/**
 * Universal Auth Hook
 *
 * Provides a unified interface for authentication that works with:
 * - OIDC providers (Zitadel, Keycloak)
 * - Better Auth
 * - Any custom auth provider
 *
 * Components should use this hook instead of directly using provider-specific
 * hooks to ensure compatibility with any auth provider.
 */

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

/**
 * Unified user representation across auth providers
 */
export interface UniversalUser {
  /** User's unique identifier (sub claim or user id) */
  id: string;
  /** User's email address */
  email?: string;
  /** User's display name */
  name?: string;
  /** User's profile picture URL */
  picture?: string;
  /** Whether email is verified */
  emailVerified?: boolean;
  /** Raw user object from the auth provider */
  raw: unknown;
}

/**
 * Unified auth context value
 */
export interface UniversalAuthContextValue {
  /** Current authenticated user, or null if not authenticated */
  user: UniversalUser | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is loading */
  isLoading: boolean;
  /** Authentication error, if any */
  error: Error | null;
  /** The type of auth provider being used */
  authType: "oidc" | "better-auth" | "unknown";
  /** Sign in (for Better Auth only - OIDC uses redirect) */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign up (for Better Auth only) */
  signUp: (email: string, password: string, name: string) => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Redirect to sign in (for OIDC) */
  signInRedirect: () => void;
  /** Redirect to sign out (for OIDC) */
  signOutRedirect: (postLogoutRedirectUri?: string) => void;
}

/**
 * Context for universal auth - exported so auth providers can create their own wrappers
 */
export const UniversalAuthContext =
  createContext<UniversalAuthContextValue | null>(null);

/**
 * Hook to access the universal auth context.
 * Works with both OIDC and Better Auth providers.
 */
export function useUniversalAuth(): UniversalAuthContextValue {
  const context = useContext(UniversalAuthContext);
  if (!context) {
    throw new Error(
      "useUniversalAuth must be used within a UniversalAuthProvider",
    );
  }
  return context;
}

/**
 * Provider that wraps OIDC auth and exposes a universal interface.
 * Used by OIDC-based providers (Zitadel, Keycloak, generic OIDC).
 */
export function OidcUniversalAuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const oidc = useOidcAuth();

  const value = useMemo<UniversalAuthContextValue>(() => {
    const user: UniversalUser | null = oidc.user
      ? {
          id: oidc.user.profile.sub,
          email: oidc.user.profile.email,
          name:
            oidc.user.profile.name ??
            oidc.user.profile.preferred_username ??
            undefined,
          picture: oidc.user.profile.picture,
          emailVerified: oidc.user.profile.email_verified,
          raw: oidc.user,
        }
      : null;

    return {
      user,
      isAuthenticated: oidc.isAuthenticated,
      isLoading: oidc.isLoading,
      error: oidc.error ?? null,
      authType: "oidc",
      signIn: async () => {
        // OIDC uses redirect-based auth
        await oidc.signinRedirect();
      },
      signUp: async () => {
        // OIDC typically doesn't support direct signup
        throw new Error("Direct signup not supported with OIDC");
      },
      signOut: async () => {
        await oidc.signoutRedirect({
          post_logout_redirect_uri: window.location.origin,
        });
      },
      signInRedirect: () => {
        void oidc.signinRedirect();
      },
      signOutRedirect: (postLogoutRedirectUri?: string) => {
        void oidc.signoutRedirect({
          post_logout_redirect_uri:
            postLogoutRedirectUri ?? window.location.origin,
        });
      },
    };
  }, [oidc]);

  return (
    <UniversalAuthContext.Provider value={value}>
      {children}
    </UniversalAuthContext.Provider>
  );
}

/**
 * Check if the current auth type is Better Auth
 */
export function useIsBetterAuth(): boolean {
  const context = useContext(UniversalAuthContext);
  return context?.authType === "better-auth";
}

/**
 * Check if the current auth type is OIDC
 */
export function useIsOidcAuth(): boolean {
  const context = useContext(UniversalAuthContext);
  return context?.authType === "oidc";
}

/**
 * Default sign-in path for each auth type
 */
function getSignInPath(authType: "oidc" | "better-auth" | "unknown"): string {
  switch (authType) {
    case "better-auth":
      return "/auth/sign-in";
    case "oidc":
      // OIDC uses redirect, but we can still have a loading page
      return "/auth/sign-in";
    default:
      return "/auth/sign-in";
  }
}

/**
 * Hook to require authentication on a page.
 * Redirects to the sign-in page if not authenticated.
 *
 * Works with all auth providers (OIDC, Better Auth, Keycloak, Zitadel).
 *
 * @param options.signInPath - Custom sign-in path (defaults based on auth type)
 * @param options.returnUrl - Custom return URL after login (defaults to current URL)
 *
 * @example
 * ```tsx
 * function ProtectedPage() {
 *   const { isLoading, isAuthenticated } = useRequireAuth();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (!isAuthenticated) return null; // Will redirect
 *
 *   return <ProtectedContent />;
 * }
 * ```
 */
export function useRequireAuth(options?: {
  signInPath?: string;
  returnUrl?: string;
}): {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UniversalUser | null;
} {
  const auth = useUniversalAuth();
  const signInPath = options?.signInPath ?? getSignInPath(auth.authType);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      // Store the current URL to redirect back after login
      const returnUrl =
        options?.returnUrl ?? window.location.pathname + window.location.search;

      // Don't store auth pages as return URLs
      if (!returnUrl.startsWith("/auth/")) {
        sessionStorage.setItem("authReturnUrl", returnUrl);
      }

      // For OIDC, use the redirect flow
      if (auth.authType === "oidc") {
        auth.signInRedirect();
      } else {
        // For Better Auth and others, navigate to sign-in page
        window.location.href = signInPath;
      }
    }
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.authType,
    auth.signInRedirect,
    signInPath,
    options?.returnUrl,
  ]);

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
  };
}

/**
 * Hook to redirect authenticated users away from auth pages.
 * Use on login/signup pages to redirect to the app when already logged in.
 *
 * Works with all auth providers (OIDC, Better Auth, Keycloak, Zitadel).
 *
 * @param redirectTo - Where to redirect authenticated users (default: "/" or stored return URL)
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const { isLoading, isAuthenticated } = useRedirectIfAuthenticated();
 *
 *   if (isLoading || isAuthenticated) return <LoadingSpinner />;
 *
 *   return <LoginForm />;
 * }
 * ```
 */
export function useRedirectIfAuthenticated(
  redirectTo = "/",
  options?: { disabled?: boolean },
): {
  isAuthenticated: boolean;
  isLoading: boolean;
} {
  const auth = useUniversalAuth();
  const disabled = options?.disabled ?? false;

  useEffect(() => {
    if (disabled) return;
    if (!auth.isLoading && auth.isAuthenticated) {
      // Check for stored return URL from before login
      const storedReturnUrl = sessionStorage.getItem("authReturnUrl");
      const targetUrl = storedReturnUrl ?? redirectTo;
      sessionStorage.removeItem("authReturnUrl");

      window.location.href = targetUrl;
    }
  }, [auth.isAuthenticated, auth.isLoading, redirectTo, disabled]);

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
  };
}
